import { forgetPrograms } from "./shaderProgram";

const CONTEXT_ATTRIBUTES = {
  antialias: true,
  alpha: false,
  depth: false,
  stencil: false,
  preserveDrawingBuffer: false,
};
// A lost context not restored within this time is given up (glLayer switches to Canvas 2D)
const CONTEXT_RESTORE_TIMEOUT_MS = 5000;

/**
 * WebGL2 drawing surface of the geometry layer. Survives a lost context:
 * the browser is asked to restore it (`preventDefault`), and on restore
 * every renderer rebuilds its GPU resources from the data it keeps in JS.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {object} params
 * @param {Array<{ init: (gl: WebGL2RenderingContext) => void }>} params.renderers - read again
 *   on restore, so renderers added later (initRenderer) are restored too
 * @param {() => void} params.onRestored - the context is back, the layer can draw again
 * @param {() => void} params.onRestoreFailed - the context is not back in time or its programs failed
 * @returns {object|null} null without WebGL2 or when the shaders do not build
 */
export const createWebglSurface = (canvas, { renderers, onRestored, onRestoreFailed }) => {
  const gl = canvas.getContext("webgl2", CONTEXT_ATTRIBUTES);
  let restoreTimer = null;

  // False when a shader does not compile or link: the device cannot draw the chart with WebGL
  const initResources = () => {
    try {
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      forgetPrograms(gl);
      renderers.forEach((renderer) => renderer.init(gl));

      return true;
    } catch (error) {
      return false;
    }
  };

  const handleContextLost = (event) => {
    event.preventDefault();
    clearTimeout(restoreTimer);
    restoreTimer = setTimeout(onRestoreFailed, CONTEXT_RESTORE_TIMEOUT_MS);
  };

  const handleContextRestored = () => {
    clearTimeout(restoreTimer);

    if (initResources()) onRestored();
    else onRestoreFailed();
  };

  const destroy = () => {
    clearTimeout(restoreTimer);
    canvas.removeEventListener("webglcontextlost", handleContextLost);
    canvas.removeEventListener("webglcontextrestored", handleContextRestored);
    // Frees the GPU memory at once instead of waiting for garbage collection
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
  };

  if (!gl) return null;

  canvas.addEventListener("webglcontextlost", handleContextLost);
  canvas.addEventListener("webglcontextrestored", handleContextRestored);

  if (!initResources()) {
    destroy();
    return null;
  }

  return {
    /** False while the context is lost. */
    get isReady() {
      return !gl.isContextLost();
    },

    /**
     * Creates the resources of a renderer added after the start; while the
     * context is lost the restore creates them.
     *
     * @param {{ init: (gl: WebGL2RenderingContext) => void }} renderer
     */
    initRenderer: (renderer) => {
      if (!gl.isContextLost()) renderer.init(gl);
    },

    /** @param {number[]} background - RGBA in 0..1 */
    beginFrame: (background) => {
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.disable(gl.SCISSOR_TEST);
      gl.clearColor(...background);
      gl.clear(gl.COLOR_BUFFER_BIT);
    },

    /** @param {number[]} color - RGBA in 0..1, fills the rectangle of clipTo */
    fillClip: (color) => {
      gl.clearColor(...color);
      gl.clear(gl.COLOR_BUFFER_BIT);
    },

    /**
     * Limits drawing to a rectangle given from the top-left corner.
     *
     * @param {{ x: number, y: number, width: number, height: number }} rect - device px
     */
    clipTo: ({ x, y, width, height }) => {
      gl.enable(gl.SCISSOR_TEST);
      gl.scissor(x, canvas.height - y - height, width, height);
    },

    destroy,
  };
};
